// ════════════════════════════════════════════════════════════
//  ieLayout.js — THE SCREEN LAYOUT (The Engine · The Screen Editor, caP'n 2026-10-21)
// ════════════════════════════════════════════════════════════
//  "need that screen moving app so I can properly update games' screens for android and pc"
//
//  MASTER: gamemaster/engine/ieLayout.js — every game carries a byte-identical copy in its src/.
//  A game joins with three things:
//    1. this file in src/,   2. src/data/ie-layout.json (its pieces + placements),
//    3. one line in src/index.js:   import "./ieLayout";
//
//  WHAT IT DOES IN THE GAME (always, players included)
//    Reads the layout file, decides whether this screen is a PHONE or a PC (the file's "phone" rule),
//    and nudges / resizes / hides the listed pieces with ONE <style> tag. Nothing in the game is re-rendered,
//    no component changes, nothing is fetched: the file is part of the build. Moving uses the CSS `translate`
//    and `scale` properties, which stack on top of whatever `transform` the game already uses.
//
//  WHAT IT DOES ONLY WHILE caP'n EDITS (dev server only — gone from a production build)
//    Opened inside the Screen Editor with ?ieLayout=edit, it draws a box around every piece; drag a box to move it,
//    drag its corner to resize. It talks only to the Screen Editor page that opened it (exact origin, localhost only).
//    TOUCH PHONES (2026-10-21, for Terror): some games decide "phone" by the TOUCH SCREEN, not the width (Terror asks
//    "(pointer: coarse)"). caP'n's PC has a mouse, so its phone screen would show the PC look. A game whose file says
//    "phoneIsTouch": true gets, in the Screen Editor's PHONE screen only (&ieLayoutAs=phone), a pretend touch phone:
//    touch reported, and "(pointer: coarse)" / "(hover: none)" asked through matchMedia answer like a phone. CSS
//    @media rules in stylesheets can't be fooled this way (Terror has none). Never in a real game, never in production.
//
//  MORE KINDS OF SCREEN (caP'n 2026-10-21, Terror first) — opt-in in the layout file, so a game that says nothing is unchanged:
//    "sides": true → a phone held UPRIGHT ("phone") and SIDEWAYS ("phoneSide") are two layouts.
//    "ar": true    → while the page wears html.ie-ar (the game's AR view, on the real phone too) it is its own layout:
//                    "ar" (+ "arSide" with sides). The Screen Editor's AR screen (&ieLayoutAs=ar) is a pretend AR: a touch
//                    phone (if phoneIsTouch) with window.__ieLayoutPretendAR set before any game code, so the game can
//                    wear its AR look without a camera. Dev only, Screen Editor only.
// ════════════════════════════════════════════════════════════
import FILE from "./data/ie-layout.json";

const DEFAULT_PHONE = "(max-width: 900px), (max-height: 520px)";
const STYLE_ID = "ie-layout-style";
const LIM = { move: 100, sMin: 0.25, sMax: 4 };

let doc = isObjTop(FILE) ? FILE : {};   // the baked file (a broken file means "no layout", never a blank game)
let draft = null;            // while editing: the Screen Editor's live placements for THIS screen's kind
let editing = false;
let ghost = false;           // while ARRANGING, a hidden piece stays faintly visible so it can be found again
let mq = null;
let editor = null;           // the drag boxes (dev only, inside the Screen Editor)
const listeners = new Set();

function isObjTop(v) { return !!v && typeof v === "object" && !Array.isArray(v); }
const isObj = isObjTop;
// a selector must be plain: no braces, comments, quotes, escapes or at-rules, and brackets must balance
// (one broken selector must never be able to swallow the rules after it)
function okSelector(s) {
  if (typeof s !== "string" || !s.trim() || s.length > 300 || /[{}<@;\\"'`]|\/\*|\*\//.test(s)) return false;
  let sq = 0, rd = 0;
  for (const ch of s) {
    if (ch === "[") sq++; else if (ch === "]") sq--; else if (ch === "(") rd++; else if (ch === ")") rd--;
    if (sq < 0 || rd < 0) return false;
  }
  return sq === 0 && rd === 0;
}
const fin = (v, d) => (typeof v === "number" && isFinite(v) ? v : d);
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

/** which layout a screen uses (pure — exported for the Screen Editor's tests):
 *  phone = the file's phone rule matches · ar = the page wears html.ie-ar · side = it's wider than tall */
export function modeFor(phone, ar, side, d) {
  const o = d && typeof d === "object" ? d : {};
  const isAR = !!ar && o.ar === true;
  if (!isAR && !phone) return "desktop";
  return (isAR ? "ar" : "phone") + (o.sides === true && side ? "Side" : "");
}
let sideMq = null;
const arOn = () => typeof document !== "undefined" && !!document.documentElement && document.documentElement.classList.contains("ie-ar");
/** "phone", "desktop" — and for games that opt in, "phoneSide", "ar", "arSide" — the layout this screen uses right now */
export function layoutMode() { return modeFor(!!(mq && mq.matches), arOn(), !!(sideMq && sideMq.matches), doc); }
// ── "Make it happen" (dev only): a game can offer little scenes the Screen Editor can fire on demand, so pieces that
//    only show up in the middle of the action (banners, pop-ups, menus) can be seen, held and arranged.
//    In the game:  layoutDemo("banners.ult", "Both sides' U'hlti at once", () => { …show them… });
const demos = new Map();
export function layoutDemo(id, label, run) {
  if (process.env.NODE_ENV === "production" || typeof id !== "string" || typeof run !== "function") return;
  demos.set(id, { label: String(label || id), run });
  if (editor) editor.demosChanged();
}

/** called whenever the mode flips (a phone rotated past the line, a window resized) */
export function onLayoutMode(fn) { listeners.add(fn); return () => listeners.delete(fn); }

function placements(mode) {
  if (draft) return draft;
  const L = isObj(doc && doc.layouts) ? doc.layouts : {};
  return isObj(L[mode]) ? L[mode] : {};
}

function css(mode) {
  const pieces = isObj(doc && doc.pieces) ? doc.pieces : {};
  const P = placements(mode);
  const out = [];
  for (const id of Object.keys(pieces)) {
    const p = pieces[id], e = P[id];
    if (!isObj(p) || !isObj(e) || !okSelector(p.selector)) continue;
    const x = clamp(fin(e.x, 0), -LIM.move, LIM.move), y = clamp(fin(e.y, 0), -LIM.move, LIM.move);
    const s = clamp(fin(e.s, 1), LIM.sMin, LIM.sMax);
    const d = [];
    if (x || y) d.push(`translate:${x}vw ${y}vh`);
    if (s !== 1) d.push(`scale:${s}`);
    if (e.hide === true) d.push(ghost ? "opacity:.28!important" : "display:none!important");
    if (d.length) out.push(`${p.selector}{${d.join(";")}}`);
  }
  return out;
}

function apply() {
  if (typeof document === "undefined") return;
  let el = document.getElementById(STYLE_ID);
  if (!el) { el = document.createElement("style"); el.id = STYLE_ID; (document.head || document.documentElement).appendChild(el); }
  const rules = css(layoutMode()), text = rules.join("\n");
  if (el.dataset.was === text) return;
  el.dataset.was = text;
  // one rule at a time, so a rule the browser rejects fails on its own
  const sheet = el.sheet;
  if (!sheet) { el.textContent = text; return; }
  while (sheet.cssRules.length) sheet.deleteRule(sheet.cssRules.length - 1);
  for (const r of rules) { try { sheet.insertRule(r, sheet.cssRules.length); } catch (e) { /* that piece just isn't moved */ } }
}

// ── dev only, the Screen Editor's PHONE screen, a game that opted in: be a touch phone (see the header) ──
// Runs before init() and, because index.js imports this file FIRST, before the game's own code asks.
// The AR screen is a phone too, and also asks the game for its AR look (window.__ieLayoutPretendAR, see the header).
if (process.env.NODE_ENV !== "production" && typeof window !== "undefined" && window.parent !== window
  && /[?&]ieLayout=edit\b/.test(window.location.search)) {
  const as = (window.location.search.match(/[?&]ieLayoutAs=(phone|ar|desktop)\b/) || [])[1];
  if ((as === "phone" || as === "ar") && doc.phoneIsTouch === true) pretendTouch();
  if (as === "ar" && doc.ar === true) window.__ieLayoutPretendAR = true;
}

/** a media query as a touch phone would answer it (pure — exported for the Screen Editor's tests) */
export function asTouchPhone(q) {
  return String(q)
    .replace(/\(\s*(any-)?pointer\s*:\s*coarse\s*\)/gi, "(min-width: 0px)")
    .replace(/\(\s*(any-)?pointer\s*:\s*fine\s*\)/gi, "(max-width: 0px)")
    .replace(/\(\s*(any-)?hover\s*:\s*none\s*\)/gi, "(min-width: 0px)")
    .replace(/\(\s*(any-)?hover\s*:\s*hover\s*\)/gi, "(max-width: 0px)");
}
function pretendTouch() {
  try { if (!("ontouchstart" in window)) window.ontouchstart = null; } catch (e) { /* no */ }
  try { Object.defineProperty(navigator, "maxTouchPoints", { get: () => 5, configurable: true }); } catch (e) { /* no */ }
  if (typeof window.matchMedia === "function") {
    const real = window.matchMedia.bind(window);
    window.matchMedia = (q) => real(asTouchPhone(q));
  }
}

function init() {
  if (typeof window === "undefined" || !window.matchMedia) return;
  const rule = typeof doc.phone === "string" && doc.phone.trim() && !/[{};]|<\//.test(doc.phone) ? doc.phone : DEFAULT_PHONE;
  mq = window.matchMedia(rule);
  sideMq = window.matchMedia("(orientation: landscape)");
  let last = layoutMode();
  // re-checked whenever anything that decides it changes: the phone rule, the way it's held, the AR look
  const flip = () => {
    apply();
    const m = layoutMode();
    if (m === last) return;
    last = m;
    listeners.forEach((fn) => { try { fn(m); } catch (e) { /* a listener's problem stays its own */ } });
  };
  for (const q of [mq, sideMq]) { if (q.addEventListener) q.addEventListener("change", flip); else if (q.addListener) q.addListener(flip); }
  if (doc.ar === true && typeof MutationObserver !== "undefined" && document.documentElement) {
    new MutationObserver(flip).observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  }
  apply();
}
init();

// ── dev only: a Bake in the Screen Editor rewrites the file; take it in place instead of reloading the whole game ──
if (process.env.NODE_ENV !== "production" && typeof module !== "undefined" && module.hot) {
  module.hot.accept("./data/ie-layout.json", () => {
    const j = require("./data/ie-layout.json");
    const next = j && j.default && !j.pieces ? j.default : j;
    doc = isObj(next) ? next : {};
    if (editing && editor) editor.fileChanged();
    apply();
  });
}

// ════════════════════════════════════════════════════════════
//  THE EDITOR (dev only) — the boxes caP'n drags inside the Screen Editor
// ════════════════════════════════════════════════════════════
if (process.env.NODE_ENV !== "production" && typeof window !== "undefined" && window.parent !== window && /[?&]ieLayout=edit\b/.test(window.location.search)) {
  editor = startEditor();
}

function startEditor() {
  let from = null;
  try {
    const u = new URL(new URLSearchParams(window.location.search).get("from") || "");
    if ((u.protocol === "http:" || u.protocol === "https:") && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(u.hostname)) from = u.origin;
  } catch (e) { /* no usable "from": the editor stays silent */ }
  if (!from) return null;
  editing = true;

  const post = (m) => { try { window.parent.postMessage(Object.assign({ ie: "layout" }, m), from); } catch (e) { /* the Screen Editor went away */ } };
  let arrange = false, selected = null, accent = "#f2c14e", drag = null, seenKey = "", zoom = 1;
  // the Screen Editor shows this screen shrunk; labels and handles are drawn bigger by the same amount so they stay readable
  const setZoom = (z) => { if (typeof z === "number" && z > 0.05 && z <= 4) { zoom = z; root.style.setProperty("--z", String(z)); } };
  const setArrange = (on) => { arrange = !!on; ghost = arrange; root.classList.toggle("off", !arrange); apply(); };
  const boxes = new Map();

  // two copies of a game side by side would both play music: the Screen Editor keeps ONE quiet (dev only, editing only)
  let quiet = false;
  const playing = new Set();
  if (window.HTMLMediaElement) {
    const play = window.HTMLMediaElement.prototype.play;
    window.HTMLMediaElement.prototype.play = function () { playing.add(this); if (quiet) this.muted = true; return play.apply(this, arguments); };
  }
  // the Screen Editor can hand the music to another screen (one was closed), so quiet goes both ways
  let wasQuiet = false;
  const hush = () => { playing.forEach((a) => { if (quiet) a.muted = true; else if (wasQuiet) a.muted = false; }); wasQuiet = quiet; };

  const root = document.createElement("div");
  root.id = "ie-layout-edit";
  const sheet = document.createElement("style");
  sheet.textContent = `
#ie-layout-edit{position:fixed;inset:0;z-index:2147483646;pointer-events:none;--c:#f2c14e;--z:1}
#ie-layout-edit.off{display:none}
#ie-layout-edit .b{position:fixed;box-sizing:border-box;border:calc(1.5px / var(--z)) dashed var(--c);border-radius:3px;background:color-mix(in srgb,var(--c) 7%,transparent);pointer-events:auto;cursor:grab;touch-action:none}
#ie-layout-edit .b.sel{border-style:solid;border-width:calc(2px / var(--z));background:color-mix(in srgb,var(--c) 14%,transparent);z-index:2}
#ie-layout-edit .b.hid{border-color:#ff4d8d;background:rgba(255,77,141,.08)}
#ie-layout-edit .b.drag{cursor:grabbing}
#ie-layout-edit .b>i{position:absolute;left:calc(-1.5px / var(--z));bottom:100%;font:600 calc(11px / var(--z))/calc(16px / var(--z)) system-ui,sans-serif;font-style:normal;background:var(--c);color:#130b1c;padding:0 calc(6px / var(--z));border-radius:3px 3px 0 0;white-space:nowrap;pointer-events:none;max-width:calc(260px / var(--z));overflow:hidden;text-overflow:ellipsis}
#ie-layout-edit .b.hid>i{background:#ff4d8d}
#ie-layout-edit .b>i{display:none}
#ie-layout-edit .b:hover>i,#ie-layout-edit .b.sel>i{display:block}
#ie-layout-edit .b.rt>i{left:auto;right:calc(-1.5px / var(--z))}
#ie-layout-edit .b.low>i{bottom:auto;top:100%;border-radius:0 0 3px 3px}
#ie-layout-edit .h{position:absolute;right:calc(-8px / var(--z));bottom:calc(-8px / var(--z));width:calc(14px / var(--z));height:calc(14px / var(--z));border-radius:50%;background:var(--c);border:calc(2px / var(--z)) solid #130b1c;cursor:nwse-resize;display:none}
#ie-layout-edit .b.sel .h{display:block}`;
  document.head.appendChild(sheet);
  const mount = () => { if (document.body && !root.isConnected) document.body.appendChild(root); };
  mount();

  const pieces = () => (isObj(doc.pieces) ? doc.pieces : {});
  const entry = (id) => Object.assign({ x: 0, y: 0, s: 1, hide: false }, (draft || {})[id] || {});
  const tidy = (e) => {
    const o = {};
    const x = Math.round(clamp(e.x, -LIM.move, LIM.move) * 10) / 10, y = Math.round(clamp(e.y, -LIM.move, LIM.move) * 10) / 10;
    const s = Math.round(clamp(e.s, LIM.sMin, LIM.sMax) * 100) / 100;
    if (x) o.x = x; if (y) o.y = y; if (s !== 1) o.s = s; if (e.hide) o.hide = true;
    return o;
  };
  function setEntry(id, e) {
    const t = tidy(e);
    draft = Object.assign({}, draft || {});
    if (Object.keys(t).length) draft[id] = t; else delete draft[id];
    apply();
    post({ type: "change", mode: layoutMode(), id, entry: t });
  }

  function find(sel) {
    let list;
    try { list = document.querySelectorAll(sel); } catch (e) { return null; }
    for (const el of list) {
      if (root.contains(el)) continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) return { el, r };
    }
    return null;
  }

  function measure() {
    mount();
    const P = pieces(), seen = [];
    for (const id of Object.keys(P)) {
      const p = P[id], hit = okSelector(p.selector) ? find(p.selector) : null;
      // a piece the layout hides is still "on this screen" (it's just hidden), so the list doesn't grey it
      let there = !!hit;
      if (!there && entry(id).hide && okSelector(p.selector)) { try { there = !!document.querySelector(p.selector); } catch (e) { /* no */ } }
      seen.push(there ? 1 : 0);
      let b = boxes.get(id);
      if (!hit || !arrange) { if (b) b.style.display = "none"; continue; }
      if (!b) {
        b = document.createElement("div"); b.className = "b"; b.dataset.id = id;
        b.innerHTML = "<i></i><b class=\"h\"></b>";
        b.addEventListener("pointerdown", down);
        root.appendChild(b); boxes.set(id, b);
      }
      const r = hit.r, e = entry(id);
      b.style.display = "block";
      b.style.left = r.left + "px"; b.style.top = r.top + "px"; b.style.width = Math.max(10, r.width) + "px"; b.style.height = Math.max(10, r.height) + "px";
      b.classList.toggle("sel", id === selected);
      b.classList.toggle("hid", !!e.hide);
      b.classList.toggle("low", r.top < 20 / zoom);
      b.classList.toggle("rt", r.left > window.innerWidth / 2);   // labels on the right half hang leftwards, so they never run off the screen
      const name = (p.label || id) + (e.hide ? " · hidden" : "");
      if (b.firstChild.textContent !== name) b.firstChild.textContent = name;
    }
    for (const [id, b] of boxes) if (!P[id]) { b.remove(); boxes.delete(id); }
    const key = layoutMode() + ":" + seen.join("");
    if (key !== seenKey) { seenKey = key; post({ type: "seen", mode: layoutMode(), on: Object.keys(P).filter((_, i) => seen[i]) }); }
  }

  function down(ev) {
    if (!arrange || ev.button > 0) return;
    ev.preventDefault(); ev.stopPropagation();
    const b = ev.currentTarget, id = b.dataset.id;
    if (selected !== id) { selected = id; post({ type: "select", id }); }
    const r = b.getBoundingClientRect();
    drag = { id, b, mode: ev.target.classList.contains("h") ? "size" : "move", x0: ev.clientX, y0: ev.clientY, e0: entry(id), w: Math.max(10, r.width), h: Math.max(10, r.height), moved: false };
    try { b.setPointerCapture(ev.pointerId); } catch (e) { /* fine without */ }
    b.classList.add("drag");
    b.addEventListener("pointermove", move);
    b.addEventListener("pointerup", up);
    b.addEventListener("pointercancel", up);
    measure();
  }
  function move(ev) {
    if (!drag) return;
    ev.preventDefault(); ev.stopPropagation();
    const dx = ev.clientX - drag.x0, dy = ev.clientY - drag.y0;
    if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 3) return;      // a plain click only selects
    drag.moved = true;
    const e = Object.assign({}, drag.e0);
    if (drag.mode === "move") {
      e.x = drag.e0.x + (dx / window.innerWidth) * 100;
      e.y = drag.e0.y + (dy / window.innerHeight) * 100;
    } else {
      // the piece grows from its middle, so its corner travels half the growth: double it back
      // the corner travels half the growth (it grows from its middle): follow whichever way the hand moved most
      const g = Math.abs(dx / drag.w) >= Math.abs(dy / drag.h) ? dx / drag.w : dy / drag.h;
      e.s = drag.e0.s * (1 + 2 * g);
    }
    setEntry(drag.id, e);
    measure();
  }
  function up(ev) {
    if (!drag) return;
    const d = drag; drag = null;
    d.b.classList.remove("drag");
    d.b.removeEventListener("pointermove", move); d.b.removeEventListener("pointerup", up); d.b.removeEventListener("pointercancel", up);
    try { d.b.releasePointerCapture(ev.pointerId); } catch (e) { /* fine */ }
    if (d.moved) post({ type: "done", id: d.id });
    measure();
  }

  // arrow keys nudge the chosen piece (Shift = bigger steps); they never reach the game while arranging
  window.addEventListener("keydown", (ev) => {
    if (!arrange) return;
    if (ev.key === "Escape" && selected) { selected = null; post({ type: "select", id: null }); measure(); return; }
    if (!selected || !/^Arrow(Left|Right|Up|Down)$/.test(ev.key)) return;
    ev.preventDefault(); ev.stopImmediatePropagation();
    const step = ev.shiftKey ? 1 : 0.1, e = entry(selected);
    if (ev.key === "ArrowLeft") e.x -= step; if (ev.key === "ArrowRight") e.x += step;
    if (ev.key === "ArrowUp") e.y -= step; if (ev.key === "ArrowDown") e.y += step;
    setEntry(selected, e); post({ type: "done", id: selected }); measure();
  }, true);

  window.addEventListener("message", (ev) => {
    if (ev.source !== window.parent || ev.origin !== from) return;
    const m = ev.data;
    if (!m || m.ie !== "layout") return;
    if (m.type === "setup" || m.type === "placements") {
      if (m.mode !== layoutMode()) return;   // meant for the other kind of screen (this one just flipped): the Screen Editor resends
      if (isObj(m.placements)) { draft = m.placements; apply(); }
      if (m.type === "setup") {
        setArrange(m.arrange); selected = m.selected || null; setZoom(m.zoom);
        if (typeof m.accent === "string" && /^#[0-9a-f]{3,8}$/i.test(m.accent)) accent = m.accent;
        root.style.setProperty("--c", accent);
        quiet = !!m.quiet; hush();
      }
      seenKey = ""; measure();
    } else if (m.type === "arrange") {
      setArrange(m.on); measure();
    } else if (m.type === "zoom") {
      setZoom(m.z);
    } else if (m.type === "demo" && typeof m.id === "string" && demos.has(m.id)) {
      try { demos.get(m.id).run(); } catch (e) { console.warn("[ieLayout] that scene failed:", e); }
    } else if (m.type === "select") {
      selected = m.id || null; measure();
    }
  });

  const demoList = () => [...demos].map(([id, d]) => ({ id, label: d.label }));
  const hello = () => post({ type: "hello", mode: layoutMode(), pieces: Object.keys(pieces()), demos: demoList(), w: window.innerWidth, h: window.innerHeight });
  onLayoutMode(() => { draft = null; apply(); hello(); });   // the Screen Editor answers with this kind's placements
  window.addEventListener("resize", () => { measure(); });
  const tick = setInterval(measure, 200);
  window.addEventListener("pagehide", () => clearInterval(tick));
  hello();
  measure();
  return { fileChanged() { hello(); }, demosChanged() { post({ type: "demos", demos: demoList() }); } };
}
